import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../components/AppShell';
import AdminTabs from '../../components/AdminTabs';
import FormShell from '../../components/FormShell';
import ErrorBanner from '../../components/ErrorBanner';
import { supabase } from '../../lib/supabase';
import type { Tip, TipStatus } from '@viet-parenting/supabase/types';

interface Props {
  mode: 'edit';
}

const inputClass =
  'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500';

export default function AdminTipForm(_props: Props) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [tip, setTip] = useState<Tip | null>(null);
  const [note, setNote] = useState('');
  const [status, setStatus] = useState<TipStatus>('pending');
  const [reply, setReply] = useState('');
  const [repliedAt, setRepliedAt] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let active = true;
    (async () => {
      const { data, error: dbError } = await supabase
        .from('tips')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (!active) return;
      if (dbError || !data) {
        setError(dbError?.message ?? 'Không tìm thấy gợi ý.');
        setLoading(false);
        return;
      }
      const row = data as Tip;
      setTip(row);
      setNote(row.admin_note ?? '');
      setStatus(row.status);
      setReply(row.reply_message ?? '');
      setRepliedAt(row.replied_at);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [id]);

  const persist = async (payload: Record<string, unknown>): Promise<boolean> => {
    if (!id) return false;
    const { error: dbError } = await supabase.from('tips').update(payload).eq('id', id);
    if (dbError) {
      setError(dbError.message);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const ok = await persist({
      admin_note: note.trim() || null,
      status,
      reply_message: reply.trim() || null,
      replied_at: repliedAt,
    });
    setSaving(false);
    if (ok) navigate('/admin/tips');
  };

  const handleMarkReplied = async () => {
    // NOTE: actual email sending is deferred. When a `send-tip-reply` edge
    // function exists, invoke it here before marking as replied, e.g.:
    //   await supabase.functions.invoke('send-tip-reply', {
    //     body: { tipId: id, to: tip?.submitter_email, message: reply },
    //   });
    setSaving(true);
    setError(null);
    const now = new Date().toISOString();
    const ok = await persist({ reply_message: reply.trim() || null, replied_at: now });
    setSaving(false);
    if (ok) setRepliedAt(now);
  };

  const handleDelete = async () => {
    if (!id) return;
    const ok = window.confirm('Xóa gợi ý này? Hành động này không thể hoàn tác.');
    if (!ok) return;
    setDeleting(true);
    setError(null);
    const { error: dbError } = await supabase.from('tips').delete().eq('id', id);
    setDeleting(false);
    if (dbError) {
      setError(dbError.message);
      return;
    }
    navigate('/admin/tips');
  };

  if (loading) {
    return (
      <AppShell>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
          <AdminTabs active="tips" />
          <FormShell title="Chi tiết gợi ý" backTo="/admin/tips" maxWidth="max-w-3xl">
            <p className="text-sm text-gray-400">Đang tải…</p>
          </FormShell>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <AdminTabs active="tips" />
        <FormShell title="Chi tiết gợi ý" backTo="/admin/tips" maxWidth="max-w-3xl">
          <ErrorBanner message={error} />

          {tip && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Submission (read-only) */}
              <section className="brand-card p-6 space-y-4">
                <h2 className="font-semibold">Nội dung gửi</h2>
                <p className="whitespace-pre-wrap text-sm text-gray-800">{tip.tip}</p>
                <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm pt-2 border-t border-gray-100">
                  <Detail label="Tên" value={tip.submitter_name} />
                  <Detail
                    label="Email"
                    value={
                      tip.submitter_email ? (
                        <a href={`mailto:${tip.submitter_email}`} className="text-red-700 hover:underline">
                          {tip.submitter_email}
                        </a>
                      ) : null
                    }
                  />
                  <Detail label="Quốc gia" value={tip.country} />
                  <Detail label="Gửi lúc" value={new Date(tip.created_at).toLocaleString('vi-VN')} />
                </dl>
              </section>

              {/* Editor note */}
              <section className="brand-card p-6 space-y-3">
                <h2 className="font-semibold">Ghi chú nội bộ</h2>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="Ghi chú cho ban biên tập…"
                  className={inputClass}
                />
              </section>

              {/* Reply (sending deferred) */}
              <section className="brand-card p-6 space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <h2 className="font-semibold">Trả lời người gửi</h2>
                  {repliedAt ? (
                    <span className="text-xs text-emerald-600">
                      ✉ Đã đánh dấu trả lời · {new Date(repliedAt).toLocaleString('vi-VN')}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">Chưa trả lời</span>
                  )}
                </div>
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={4}
                  placeholder="Soạn lời cảm ơn gửi tới người đóng góp…"
                  className={inputClass}
                />
                <button
                  type="button"
                  onClick={handleMarkReplied}
                  disabled={saving || deleting}
                  className="border border-emerald-200 text-emerald-700 text-sm font-medium px-4 py-2 rounded-lg hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50 transition-colors"
                >
                  {repliedAt ? 'Cập nhật & đánh dấu đã trả lời' : 'Đánh dấu đã trả lời'}
                </button>
                <p className="text-xs text-gray-400">
                  Gửi email tự động sẽ được bổ sung sau — hiện tại chỉ lưu nội dung và đánh dấu.
                </p>
              </section>

              {/* Footer: status + actions */}
              <div className="flex items-center gap-3 flex-wrap pt-2">
                <button
                  type="button"
                  onClick={() => navigate('/admin/tips')}
                  className="border border-gray-200 text-gray-700 text-sm font-medium px-5 py-2.5 rounded-lg hover:border-red-300 transition-colors"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting || saving}
                  className="border border-red-200 text-red-700 text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-red-50 hover:border-red-300 disabled:opacity-50 transition-colors"
                >
                  {deleting ? 'Đang xóa…' : 'Xóa'}
                </button>
                <div className="flex items-center gap-4 ml-auto text-sm flex-wrap">
                  <span className="font-medium text-gray-700">Trạng thái:</span>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="status"
                      checked={status === 'pending'}
                      onChange={() => setStatus('pending')}
                    />
                    Chờ xử lý
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="status"
                      checked={status === 'done'}
                      onChange={() => setStatus('done')}
                    />
                    Đã xong
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={saving || deleting}
                  className="bg-red-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-red-800 disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Đang lưu…' : 'Lưu thay đổi'}
                </button>
              </div>
            </form>
          )}
        </FormShell>
      </div>
    </AppShell>
  );
}

interface DetailProps {
  label: string;
  value: React.ReactNode;
}
function Detail({ label, value }: DetailProps) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-400">{label}</dt>
      <dd className="text-gray-800">{value || '—'}</dd>
    </div>
  );
}
